// resources/front/default/js/pattern-showcase.js
//
// Widget de démonstration des patterns de sections.
// Détecte chaque <section class="pattern-example">, injecte une barre
// sticky (titre + onglets Aperçu / HTML / Copier) et gère le toggle.
//
// Vue HTML : re-indentée + colorée (mini-highlighter maison, zéro dépendance).
// Titre    : data-pattern-title (accents OK) → sinon id en Title Case.
// Idempotent : re-init sûr après navigation hx-boost (htmx:load).
// Import   : `import './pattern-showcase.js';` dans js/app.js

const READY_ATTR = 'data-pattern-ready';

const VOID_TAGS = new Set([
    'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
    'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** "hero-01-centre-classique" → "Hero 01 Centre Classique" (fallback). */
function titleFromId(id) {
    if (!id) return 'Pattern';
    return id
        .replace(/^section-/, '')
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Re-indente un HTML (potentiellement minifié sur une seule ligne).
 * Heuristique d'affichage : 2 espaces par niveau ; balises void et
 * balises ouvertes+fermées sur la même ligne ne décalent pas l'indent.
 */
function formatHtml(html) {
    const normalized = html
        .replace(/>\s+</g, '><')   // purge l'indentation source existante
        .replace(/></g, '>\n<');   // un tag par ligne

    let indent = 0;
    const pad = (n) => '  '.repeat(Math.max(n, 0));
    const out = [];

    for (const raw of normalized.split('\n')) {
        const line = raw.trim();
        if (!line) continue;

        const isClosing = /^<\//.test(line);
        const tagMatch = line.match(/^<([a-zA-Z][\w-]*)/);
        const tag = tagMatch ? tagMatch[1].toLowerCase() : null;
        const isSelfClose = /\/>$/.test(line);
        const isVoid = tag && VOID_TAGS.has(tag);
        const opensAndCloses = tag && new RegExp('</' + tag + '>$').test(line);

        if (isClosing) indent--;
        out.push(pad(indent) + line);
        if (!isClosing && tag && !isSelfClose && !isVoid && !opensAndCloses) indent++;
    }

    return out.join('\n');
}

/**
 * Coloration syntaxique : segmente balises / texte, échappe puis enveloppe
 * dans des <span class="tok-*"> stylés via le CSS.
 */
function colorize(formatted) {
    return formatted.replace(/(<[^>]+>)|([^<]+)/g, (m, tagSeg, textSeg) => {
        if (textSeg != null) {
            return '<span class="tok-text">' + escapeHtml(textSeg) + '</span>';
        }

        let inner = tagSeg.slice(1, -1); // retire < >
        const selfSlash = inner.endsWith('/') ? '/' : '';
        if (selfSlash) inner = inner.slice(0, -1);
        const closing = inner.startsWith('/');
        if (closing) inner = inner.slice(1);

        const sp = inner.match(/^([a-zA-Z][\w-]*)([\s\S]*)$/);
        if (!sp) return '<span class="tok-punct">' + escapeHtml(tagSeg) + '</span>';

        const name = sp[1];
        const attrs = sp[2].replace(
            /([a-zA-Z_:][\w:.-]*)(\s*=\s*)("[^"]*"|'[^']*')/g,
            (mm, an, eq, av) =>
                '<span class="tok-attr">' + escapeHtml(an) + '</span>' +
                escapeHtml(eq) +
                '<span class="tok-str">' + escapeHtml(av) + '</span>'
        );

        return (
            '<span class="tok-punct">&lt;' + (closing ? '/' : '') + '</span>' +
            '<span class="tok-tag">' + escapeHtml(name) + '</span>' +
            attrs +
            '<span class="tok-punct">' + selfSlash + '&gt;</span>'
        );
    });
}

function buildBar(title) {
    const bar = document.createElement('div');
    bar.className = 'pattern-bar';

    const label = document.createElement('span');
    label.className = 'pattern-bar__title';
    label.textContent = title;

    const tabs = document.createElement('div');
    tabs.className = 'pattern-bar__tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.innerHTML =
        '<button type="button" class="pattern-tab is-active" data-view="preview">Aperçu</button>' +
        '<button type="button" class="pattern-tab" data-view="code">HTML</button>' +
        '<button type="button" class="pattern-tab" data-view="copy">Copier</button>';

    bar.appendChild(label);
    bar.appendChild(tabs);
    return bar;
}

function initSection(section) {
    if (section.hasAttribute(READY_ATTR)) return;

    // 1. HTML pristine AVANT mutation, puis version indentée (affichage + copie)
    const rawHtml = section.outerHTML;
    section.setAttribute(READY_ATTR, '');
    const formatted = formatHtml(rawHtml);

    // 2. Titre
    const title = section.dataset.patternTitle || titleFromId(section.id);

    // 3. Wrapper + barre + bloc code coloré
    const block = document.createElement('div');
    block.className = 'pattern-block';

    const bar = buildBar(title);

    const pre = document.createElement('pre');
    pre.className = 'pattern-code';
    pre.hidden = true;
    const code = document.createElement('code');
    code.className = 'language-html';
    code.innerHTML = colorize(formatted);
    pre.appendChild(code);

    section.parentNode.insertBefore(block, section);
    block.appendChild(bar);
    block.appendChild(section);
    block.appendChild(pre);

    // 4. Onglets
    const tabButtons = bar.querySelectorAll('.pattern-tab');
    tabButtons.forEach((tab) => {
        tab.addEventListener('click', async () => {
            const view = tab.dataset.view;

            if (view === 'copy') {
                try {
                    await navigator.clipboard.writeText(formatted);
                    const prev = tab.textContent;
                    tab.textContent = 'Copié ✓';
                    setTimeout(() => { tab.textContent = prev; }, 1500);
                } catch (e) {
                    const prev = tab.textContent;
                    tab.textContent = 'Erreur';
                    setTimeout(() => { tab.textContent = prev; }, 1500);
                }
                return;
            }

            tabButtons.forEach((t) => {
                if (t.dataset.view !== 'copy') t.classList.toggle('is-active', t === tab);
            });
            const showCode = view === 'code';
            section.hidden = showCode;
            pre.hidden = !showCode;
        });
    });
}

/** Scanne et initialise toutes les sections pattern. Idempotent. */
export function initPatternShowcase(root = document) {
    root.querySelectorAll('section.pattern-example').forEach(initSection);
}

document.addEventListener('DOMContentLoaded', () => initPatternShowcase());
document.addEventListener('htmx:load', () => initPatternShowcase());